import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { catchError, of, tap } from 'rxjs';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { EnumDescripcionTcTipoSolicitudCamPlural, EnumDescripcionTcTipoSolicitudCamSingular, EnumIdsTcTipoSolicitudCam } from '../../../types/enum';
import { ConfirmationService } from 'primeng/api';
import { PeriodoCamService } from 'src/app/components/_mantenimiento/campanias/periodos/datosbasicos/periodocam.service';
import { PeriodoCamViewModel } from 'src/app/components/_mantenimiento/campanias/periodos/datosbasicos/periodocam.types';
import { TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';
import { RecintoUnidadInspeccionItemViewModel } from '../../../buscadores/unidades-inspeccion/buscador-unidad-inspeccion.types';

@Component({
    selector: 'app-unidad-inspeccion-wrapper',
    templateUrl: './unidad-inspeccion-wrapper.component.html',
    styleUrls: ['./unidad-inspeccion-wrapper.component.scss'],
})
export class UnidadInspeccionWrapperComponent extends ComponentBase implements OnInit {
    visibilitySpinnerGuardando = false;
    unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    recintoUnidadInspeccionList: RecintoUnidadInspeccionItemViewModel[];
    formGroup: FormGroup;

    // Recinto/Almacén/Empresa-Deshidratadora Detail view params
    recintoDetailVisible = false;
    almacenDetailVisible = false;
    empresaDetailVisible = false;

    recintoPIAIdSelected: number | null;
    superficieAcumulada: number;
    periodoForUi: PeriodoCamViewModel;

    private listUrl = '/campanias/unidadesinspeccion';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idUi: any = params.get('idUi');
        if (idUi) {
            this.getUnidadInspeccion(idUi);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
        private periodoCamService: PeriodoCamService,
        protected authService: AuthService,
        protected notificationService: NotificationService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Campania: [null],
            Periodo: [null],
            DescripcionComunidad: [null],
            EntidadAuditora: [null],
            TipoRecinto: [null], // Tipo de Solicitud
            Operador: [null],
        });
        this.form = this.formGroup;
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PIAEd" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de datos del periodo.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    // Una UI existente deberá tener al menos un recinto PIAEd.
                    if (!udInspeccion?.FirstRecinto) {
                        // No debería pasar por aquí, porque cuando se desvinculan todos los recintos de una UI, se "elimina" además la UI
                        console.error('U.I. sin ' + this.title.plural);
                        // return;
                    }
                    this.unidadInspeccion = udInspeccion;
                    this.navMenuService.updateMenuServiceForUdInspeccion(
                        udInspeccion.IdUi!,
                        udInspeccion.IdEstadoUi!,
                        udInspeccion.FirstRecinto?.IdTipoSolicitudCam || 0,
                        udInspeccion.PeriodoCertificaKgs,
                    );
                    this.getRecintosUiByIdUi(this.unidadInspeccion.IdUi!);
                    this.getPeriodoInfo(this.unidadInspeccion.FirstRecinto?.IdPeriodo);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private getRecintosUiByIdUi(idUi: number): void {
        this.unidadInspeccionService
            .getRecintosUiByIdUi(idUi)
            .pipe(
                tap(res => {
                    this.recintoUnidadInspeccionList = res;
                    this.calculateSuperficieAcumulada();
                }),
            )
            .subscribe();
    }

    private calculateSuperficieAcumulada(): void {
        this.superficieAcumulada = 0;
        this.recintoUnidadInspeccionList?.forEach(recintoUI => {
            this.superficieAcumulada += recintoUI.Cultivo?.SuperficieVariedad;
        });
    }

    /**
     * A partir del periodo se obtendrá el SuperficieMax y la Superficie acumulada (por los cultivos en los recintos-UI).
     * También permite obtener el valor HabilitadaTemporalmente, usado en los permisos.
     * @param IdPeriodo number | undefined
     * @returns void
     */
    private getPeriodoInfo(IdPeriodo: number | undefined): void {
        if (!IdPeriodo) {
            return;
        }
        this.periodoCamService
            .getById(IdPeriodo)
            .pipe(
                tap((periodo: PeriodoCamViewModel) => {
                    this.periodoForUi = periodo;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    /**
     * Redigirimos al usuario a la pantalla de edición de la UI, para la selección de recintos.
     */
    onClickAdd(): void {
        this.router.navigate(['/campanias/unidadinspeccion/datosbasicos/edition', this.unidadInspeccion.IdUi]);
    }

    /**
     * Se desvinculará la UI.
     * La UI se marca como ELIMINADA, pero no se elimina físicamente.
     */
    onClickDeleteUi(): void {
        this.confirmationService.confirm({
            message: 'Si elimina la U.I., además eliminará la vinculación con sus ' + this.title.plural + ', ¿desea continuar?',
            accept: () => {
                this.desvinculaUI(this.unidadInspeccion.IdUi!);
            },
        });
    }

    /**
     * Se desvincula el recinto PI de la UI.
     * En el caso que solamente quedara un recinto PI vinculado a la UI,
     * se marca como ELIMINADA la propia UI.
     * @param idRecintoUdInspeccion number
     */
    onDelete(idRecintoUdInspeccion: number): void {
        if (this.recintoUnidadInspeccionList?.length === 1) {
            this.onClickDeleteUi();
        } else {
            this.confirmationService.confirm({
                message: 'Desvincular ' + this.title.singular + ' de la U.I., ¿desea continuar?',
                accept: () => {
                    this.desvincularRecintoUI(idRecintoUdInspeccion);
                },
            });
        }
    }

    /**
     * Si la UI es de tipo confección o empresas de deshidratación:
     * - NO SE PERMITE
     * Si la UI es de tipo PI:
     * - Desvincula UI y recintos y "elimina" la UI.
     * - La solicitud y los recintos PI siguen vinculados y no se podrán usar en otra solicitud del mismo periodo, pero si en otra UI.
     * La UI pasa al estado ELIMINADA.
     * @param idUnidadInspeccion number
     */
    private desvinculaUI(idUnidadInspeccion: number): void {
        this.visibilitySpinnerGuardando = true;
        this.unidadInspeccionService
            .desvinculaUI(idUnidadInspeccion)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerGuardando = false;
                    this.addSuccessMessage('U.I. eliminada con éxito');
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinnerGuardando = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Desvincula el recinto PI de la unidad de inspección
     * @param idRecintoUdInspeccion number
     */
    private desvincularRecintoUI(idRecintoUdInspeccion: number): void {
        this.visibilitySpinnerGuardando = true;
        this.unidadInspeccionService
            .desvincularRecintoUI(idRecintoUdInspeccion)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerGuardando = false;
                    this.addSuccessMessage('Desvinculación de ' + this.title.singular + ' de la U.I. con éxito');
                    this.getUnidadInspeccion(this.unidadInspeccion.IdUi!); // Recargamos el detalle de la Ud. de Inspección
                }),
                catchError(error => {
                    this.visibilitySpinnerGuardando = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se darán de baja los recintos PIAEd en el periodo actual.
     * No podrán ser usados en ninguna UI o solicitud en el periodo.
     * La UI pasa a estado BAJA.
     */
    onClickBajaUi(): void {
        this.confirmationService.confirm({
            message: 'Si da de Baja la U.I., se bloquearán sus ' + this.title.plural + ' en el Periodo actual, ¿desea continuar?',
            accept: () => {
                this.darDeBajaUnidadInspeccion(this.unidadInspeccion.IdUi!);
            },
        });
    }

    /**
     * Se da de baja la UI completa, con sus recintos PIAEd.
     * Esto implica que los recintos PIAEd se bloquean para solicitudes y otras UI en el periodo/campaña vigente.
     * El estado de la UI pasa a ser BAJA.
     * @param idUnidadInspeccion number
     */
    private darDeBajaUnidadInspeccion(idUnidadInspeccion: number): void {
        this.visibilitySpinnerGuardando = true;
        this.unidadInspeccionService
            .darDeBajaUI(idUnidadInspeccion)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerGuardando = false;
                    this.addSuccessMessage('U.I. dada de Baja con éxito');
                    this.getUnidadInspeccion(idUnidadInspeccion);
                }),
                catchError(error => {
                    this.visibilitySpinnerGuardando = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onClickSuspenderUi(): void {
        this.confirmationService.confirm({
            message: 'Si suspende la U.I., se bloquearán sus ' + this.title.plural + ' en la Campaña actual, ¿desea continuar?',
            accept: () => {
                this.suspenderUnidadInspeccion(this.unidadInspeccion.IdUi!);
            },
        });
    }

    /**
     * La UI pasa al estado SUSPENDIDA
     * @param idUnidadInspeccion number
     */
    private suspenderUnidadInspeccion(idUnidadInspeccion: number): void {
        this.visibilitySpinnerGuardando = true;
        this.unidadInspeccionService
            .suspenderUI(idUnidadInspeccion)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerGuardando = false;
                    this.addSuccessMessage('U.I. Suspendida con éxito');
                    this.getUnidadInspeccion(idUnidadInspeccion);
                }),
                catchError(error => {
                    this.visibilitySpinnerGuardando = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se mostrará una ventana popup con el detalle del recinto PIA, según corresponda.
     * @param itemId UnidadInspeccionItemViewModel
     * @return void
     */
    onView(item: RecintoUnidadInspeccionItemViewModel): void {
        if (item.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_INVERNADERO || item.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_PARCELA) {
            // Abrir detalle de recinto PI
            this.recintoPIAIdSelected = item.Cultivo.IdRecinto;
            this.recintoDetailVisible = true;
        } else if (item.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_CONFECCION) {
            // Abrir detalle de almacén
            this.recintoPIAIdSelected = item.Almacen.Id!;
            this.almacenDetailVisible = true;
        } else if (item.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION) {
            // Abrir detalle de empresa deshidratadora
            this.recintoPIAIdSelected = item.EmpresaDeshidratadora.Id!;
            this.empresaDetailVisible = true;
        }
    }

    get esTipoSolicitudPI(): boolean {
        return (
            this.unidadInspeccion?.FirstRecinto?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_PARCELA ||
            this.unidadInspeccion?.FirstRecinto?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_INVERNADERO
        );
    }

    /**
     * Se captura el evento que indica que la ventana de diálogo que muestra el detalle del recinto/almacén
     * se ha cerrado.
     * @param $event any
     */
    onRecintoDialogClosed($event: any): void {
        this.recintoDetailVisible = false;
        this.almacenDetailVisible = false;
        this.empresaDetailVisible = false;
    }

    get title(): any {
        if (!this.unidadInspeccion)
            return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };

        switch (this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.CONFECCION, singular: EnumDescripcionTcTipoSolicitudCamSingular.CONFECCION };
            case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.EMPRESA_DESHIDRATACION,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.EMPRESA_DESHIDRATACION,
                };
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.PARCELA,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.PARCELA,
                };
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.INVERNADERO,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO,
                };
            default:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };
        }
    }

    get allowToAdd(): boolean {
        return !this.actionNotAllowed && !this.superficieAcumuladaExcedida;
    }

    private get superficieAcumuladaExcedida(): boolean {
        if (!this.periodoForUi?.TamanioMaximoAgrupacion) {
            return false;
        }
        if (!this.periodoForUi?.SuperficieMax) {
            return false;
        }

        return this.superficieAcumulada > this.periodoForUi?.SuperficieMax;
    }

    /**
     * Este método indica si se pueden añadir recintos a la unidad de inspección.
     * Si el objeto unidadInspeccion no se ha cargado, no se le habilita el permiso.
     * Además se comprueba que el usuario (por perfil) tenga los permisos necesarios.
     * @return boolean
     */
    get hasAddingAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess =
            this.loggedInUser?.getAction(
                EnumCodigosTwTcAcciones.ADD_RECINTO_UD_INSPECCION,
                this.unidadInspeccion.IdEstadoUi,
                this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            ).Editable || false;
        return hasAccess && !this.readonly;
    }

    /**
     * En principio solo podrá borrar UIs el admin.
     */
    get hasDeleteAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess =
            this.loggedInUser?.getAction(
                EnumCodigosTwTcAcciones.REMOVE_UD_INSPECCION,
                this.unidadInspeccion?.IdEstadoUi,
                this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            ).Editable || false;
        return hasAccess && !this.readonly;
    }

    get hasBajaAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.BAJA_UD_INSPECCION, this.unidadInspeccion?.IdEstadoUi).Editable || false;
        return hasAccess && !this.readonly;
    }

    get hasSuspenderAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.SUSPENDER_UD_INSPECCION, this.unidadInspeccion?.IdEstadoUi).Editable || false;
        return hasAccess && !this.readonly;
    }

    get actionNotAllowed(): boolean {
        return this.visibilitySpinnerGuardando || this.readonly;
    }

    get readonly(): boolean {
        // PeriodoEnPlazoVigente se calcula en backend teniendo en cuenta:
        // - las fechas del periodo
        // - el campo HabilitadaTemporalmente
        return this.unidadInspeccion?.FirstRecinto ? this.unidadInspeccion.FirstRecinto?.PeriodoEnPlazoVigente === false : true;
    }
}
